import { THRESHOLDS_VERSION } from '@erectus/shared';
import type { SessionSummary } from '@erectus/shared';
import { aggregateSession } from '@/scoring/sessionScore.js';
import { appConfig } from '@/lib/remoteConfig.js';
import { deviceClass } from '@/lib/device.js';
import { readEnv } from '@/lib/env.js';
import { saveSessionSummary } from '@/lib/repo.js';
import { markStale } from '@/lib/warmCache.js';
import { db, loadFrames, purgeAfterFor, saveSnapshots } from './db.js';
import { retryKeypoints } from './upload.js';
import { minuteSeries, snapshotsFrom } from '@/scoring/hourline.js';

/**
 * Turn a finished recording into the Firestore session document
 * (docs/ARCHITECTURE.md §2.2) and queue it for upload.
 *
 * Only the summary goes to the server. Frame-level data stays on the device
 * unless the user opted in, and even then it goes to Storage as a compressed
 * file — never to Firestore, which would be both expensive and over-collection.
 */

export interface FinalizeInput {
  sessionId: string;
  uid: string;
  startedAt: number;
  endedAt: number;
  plannedMin: number;
  sampleFps: number;
  poseModel: 'lite' | 'full';
  framingScore: number;
  dataOptIn: boolean;
  disagreementCount?: number;
  /** Median signed phone roll in the picture / lean, or null without a motion sensor. */
  phoneRollDeg?: number | null;
  phonePitchDeg?: number | null;
}

export interface FinalizeResult {
  summary: SessionSummary;
  synced: boolean;
  frameCount: number;
}

export async function finalizeSession(input: FinalizeInput): Promise<FinalizeResult> {
  const frames = await loadFrames(input.sessionId);
  const config = appConfig();
  const aggregate = aggregateSession(frames, {
    weights: config.scoreWeights,
    sampleFps: input.sampleFps,
  });

  const summary: SessionSummary = {
    uid: input.uid,
    startedAt: input.startedAt,
    endedAt: input.endedAt,
    durationSec: aggregate.durationSec,
    validRatio: aggregate.validRatio,
    score: aggregate.score,
    goodRatio: aggregate.goodRatio,
    fhpMin: aggregate.fhpMin,
    slouchMin: aggregate.slouchMin,
    reclineMin: aggregate.reclineMin,
    cva: aggregate.cva,
    cha: aggregate.cha,
    trunk: aggregate.trunk,
    prot: aggregate.prot,
    hip: aggregate.hip,
    episodes: aggregate.episodes,
    decay: aggregate.decay,
    series: minuteSeries(frames),
    actualFps: aggregate.durationSec > 0 ? Math.round((frames.length / aggregate.durationSec) * 100) / 100 : 0,
    timeToFirst: aggregate.timeToFirst,
    framingScore: Math.round(input.framingScore * 1000) / 1000,
    poseModel: input.poseModel,
    appVersion: readEnv().appVersion,
    // The compiled thresholds produced these numbers; a Remote Config label must not relabel them.
    thresholdsVersion: THRESHOLDS_VERSION,
    classifierVersion: config.classifierModelVersion,
    storagePath: null,
    deviceClass: deviceClass(),
    sampleFps: input.sampleFps,
    disagreementCount: input.disagreementCount ?? 0,
    phoneRollDeg: input.phoneRollDeg ?? null,
    phonePitchDeg: input.phonePitchDeg ?? null,
  };

  // Skeletons every five minutes, kept on this device past the frame purge.
  await saveSnapshots(input.sessionId, snapshotsFrom(frames, input.sampleFps)).catch(() => undefined);

  await db.sessions.put({
    id: input.sessionId,
    uid: input.uid,
    startedAt: input.startedAt,
    endedAt: input.endedAt,
    status: 'summarized',
    plannedMin: input.plannedMin,
    sampleFps: input.sampleFps,
    poseModel: input.poseModel,
    framingScore: summary.framingScore,
    summary,
    syncedAt: null,
    uploadedAt: null,
    // Frames feed the replay for a week on this device; shared sessions replay from the account after.
    purgeAfter: purgeAfterFor(input.endedAt),
  });

  let synced = false;
  try {
    await saveSessionSummary(input.sessionId, summary);
    markStale('sessions:');
    synced = true;
    await db.sessions.update(input.sessionId, { status: 'synced', syncedAt: Date.now() });
  } catch {
    // Offline: park it in the outbox and show the report from the local copy.
    await db.outbox.add({
      kind: 'session',
      sessionId: input.sessionId,
      payload: summary,
      createdAt: Date.now(),
      attempts: 0,
    });
  }

  return { summary, synced, frameCount: frames.length };
}

/** Retry queued writes. Called on app start and when the tab comes back online. */
export async function flushOutbox(): Promise<number> {
  const items = await db.outbox.orderBy('createdAt').toArray();
  let sent = 0;
  for (const item of items) {
    if (item.id === undefined) continue;
    try {
      // Before 2026-10-07 failed keypoint uploads were queued as kind 'session',
      // where they were mistaken for summaries and never retried.
      const keypoints = item.kind === 'keypoints' || isKeypointRetry(item.payload);
      if (keypoints) {
        const outcome = await retryKeypoints(item.sessionId, item.payload);
        if (outcome === 'failed') {
          // Withdrawn consent fails at the storage rule forever: stop after a while
          // so the frames can follow the normal 7-day window.
          if (item.attempts + 1 >= MAX_KEYPOINT_ATTEMPTS) await db.outbox.delete(item.id);
          else await db.outbox.update(item.id, { kind: 'keypoints', attempts: item.attempts + 1 });
          continue;
        }
      } else if (item.kind === 'session') {
        await saveSessionSummary(item.sessionId, item.payload as SessionSummary);
        markStale('sessions:');
        await db.sessions.update(item.sessionId, { status: 'synced', syncedAt: Date.now() });
      }
      await db.outbox.delete(item.id);
      sent += 1;
    } catch {
      await db.outbox.update(item.id, { attempts: item.attempts + 1 });
    }
  }
  return sent;
}

const MAX_KEYPOINT_ATTEMPTS = 20;

function isKeypointRetry(payload: unknown): boolean {
  return typeof payload === 'object' && payload !== null && (payload as { retryUpload?: unknown }).retryUpload === true;
}

export async function localSummary(sessionId: string): Promise<SessionSummary | null> {
  const row = await db.sessions.get(sessionId);
  return row?.summary ?? null;
}
